import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { RootState } from './store/index';
import Loader from 'components/loader/loader';
import { fetchCurrentUser } from './store/authSlice';
import Dashboard from 'components/Dashboard/dashboard';
import { SocketProvider } from 'components/socket/SocketProvider';
import { routes, RouteConfig } from './routes/config';
import GlobalToast from 'components/toast/globalToast';
import './App.css';

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated: reduxAuth } = useAppSelector((state: RootState) => state.auth);

  if (!reduxAuth) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const muiTheme = createTheme({
  components: {
    MuiDataGrid: {
      defaultProps: {
        autoHeight: true,
        hideFooter: true,
        hideFooterPagination: true,
        disableRowSelectionOnClick: true,
        paginationMode: 'server',
        sortingMode: 'server',
        slotProps: {
          loadingOverlay: {
            variant: 'linear-progress',
            noRowsVariant: 'linear-progress',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'var(--text-secondary)',
        },
      },
    },
  } as any,
});

const App = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state: RootState) => state.ui.theme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const { isLoading } = useAppSelector((state: RootState) => state.auth);

  useEffect(() => {
    const wasAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (wasAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  if (isLoading) {
    return <Loader />;
  }

  const renderRoute = (route: RouteConfig) => {
    let element = <route.component />;

    if (route.useDashboard) {
      element = <Dashboard>{element}</Dashboard>;
    }

    if (route.protected) {
      element = <ProtectedRoute>{element}</ProtectedRoute>;
    }

    return element;
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <SocketProvider>
          <BrowserRouter>
            <div className="App">
              <Loader />
              <GlobalToast />
              <Routes>
                {routes.map((route) => (
                  <Route key={route.path} path={route.path} element={renderRoute(route)} />
                ))}
              </Routes>
            </div>
          </BrowserRouter>
        </SocketProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
